import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { createVehicleComparisonView } from "../../api/dossier/createVehicleComparisonView";
import { getDossier } from "../../api/dossier/getDossier";
import { getDossierStatus } from "../../api/dossier/getDossierStatus";
import { getDossierVehicleComparisons } from "../../api/dossier/getDossierVehicleComparisons";
import { patchVehicleComparisonView } from "../../api/dossier/patchVehicleComparisonView";
import AITermsText from "../../components/AITermsText";
import APButton from "../../components/buttons/APButton";
import APContactHelpButton from "../../components/buttons/APContactHelpButton";
import AIButton from "../../components/buttons/APOutlineButtons/AIButton";
import BackButton from "../../components/buttons/BackButton";
import HomeButton from "../../components/buttons/HomeButton";
import InfoButton from "../../components/buttons/InfoButton";
import APComparison, {
  Comparison,
  ComparisonCriterion,
} from "../../components/comparison/APComparison";
import APComparisonSummary from "../../components/comparison/APComparisonSummary";
import OverwriteQuoteModal from "../../components/modals/OverwriteQuoteModal";
import PageContainer from "../../components/PageContainer";
import PoweredByGemini from "../../components/PoweredByGemini";
import RestrictedAppScope from "../../components/RestrictedAppScope";
import CopyMarketingUrl from "../../components/utils/CopyMarketingUrl";
import {
  getSummarySortFn,
  parseComparisonParams,
} from "../../helpers/comparison";
import { buildQuoteQueryString } from "../../helpers/quoteParams";
import {
  formatReleaseDate,
  standardValueFormatter,
  standardVehicleValueFormatter,
} from "../../helpers/vehicle";
import { useAIConsent } from "../../hooks/useAIConsent";
import { useComparisonViewTracking } from "../../hooks/useComparisonViewTracking";
import { useFaqs } from "../../hooks/useFaqs";
import { useQuoteLimit } from "../../hooks/useQuoteLimit";
import { useAuth } from "../../providers/AuthProvider";
import {
  CriteriaLabelMap,
  VehicleComparison,
  VehicleComparisonItem,
} from "../../types/Vehicles";

const defaultSelectedCriteria: (keyof VehicleComparisonItem)[] = [
  "vehicle.price",
  "value",
  "comfort",
  "performance",
  "runningCosts",
];

const DossierVehicleComparison = () => {
  const [searchParams] = useSearchParams();
  const { dossierId } = useParams();
  const navigate = useNavigate();
  const { inAppScope, getScopedPageUrl } = useAuth();
  const { shouldPrompt: shouldPromptForAIConsent } = useAIConsent();
  const [selectedItem, setSelectedItem] =
    useState<VehicleComparisonItem | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const redbookCodes = searchParams.get("codes")?.split(",") ?? [];
  const quoteId = searchParams.get("quoteId") || undefined;
  const hideQuoteButton =
    !!searchParams.get("hideQuoteButton") &&
    searchParams.get("hideQuoteButton") === "true";
  const initialPrimaryCriteria =
    searchParams.get("primaryCriteria") ?? undefined;
  const selectedCriteria =
    searchParams.get("selectedCriteria")?.split(",") ?? defaultSelectedCriteria;

  const comparisons = getDossierVehicleComparisons(
    dossierId ?? "",
    redbookCodes
  );

  // View tracking
  const { mutateAsync: mutateCreateVehicleComparisonViewAsync } =
    createVehicleComparisonView(dossierId || "");
  const { mutateAsync: mutatePatchVehicleComparisonViewAsync } =
    patchVehicleComparisonView(dossierId || "");

  const _createView = async () => {
    const vehicleVariantIds =
      comparisons.data?.map((comparison) => comparison.variant.id) ?? [];
    const _viewId = await mutateCreateVehicleComparisonViewAsync({
      vehicleVariantIds: vehicleVariantIds,
    });

    return _viewId;
  };

  const _patchView = async (viewId: string, sortHistory: string[]) => {
    await mutatePatchVehicleComparisonViewAsync({
      vehicleComparisonViewId: viewId,
      clickedCriteria: sortHistory.join(","),
    });
  };

  const { onSort, viewId } = useComparisonViewTracking({
    createView: inAppScope("external")
      ? _createView
      : () => Promise.resolve(""),
    patchView: inAppScope("external") ? _patchView : () => Promise.resolve(),
    createViewDisabled: !comparisons.isSuccess,
  });

  const dossier = getDossier(dossierId ?? "");
  const dossierStatus = getDossierStatus(dossierId ?? "");

  if (comparisons.isError) {
    throw comparisons.error;
  }

  if (dossier.isError) {
    throw dossier.error;
  }

  if (dossierStatus.isError) {
    throw dossierStatus.error;
  }

  const {
    faqProps,
    isLoading: isLoadingFaqs,
    isSuccess: isSuccessFaqs,
  } = useFaqs({
    pageId: "vehicle-compare-view",
    contextIds: {
      opportunityId: dossier.data?.opportunity?.id,
    },
  });

  const primaryQuoteId = dossier.data?.opportunity?.primaryQuoteId;
  const {
    showOverwriteModal,
    handleQuoteAction,
    handleOverwriteConfirm,
    handleOverwriteCancel,
    oldestQuote,
  } = useQuoteLimit({
    dossierId: dossierId || "",
    primaryQuoteId: primaryQuoteId ?? null,
    onRedirect: (overwriteQuoteId?: string) => {
      if (selectedItem) {
        const { ceMakeId, ceModelId, year, ceVariantId } = selectedItem;
        const quoteParams = {
          makeId: ceMakeId,
          modelId: ceModelId,
          modelYear: year,
          variantId: ceVariantId,
          overwriteQuote: overwriteQuoteId,
          vehicleComparisonViewId: viewId ?? undefined,
        };
        const queryString = buildQuoteQueryString(quoteParams);

        if (quoteId) {
          navigate(`${getScopedPageUrl("editQuote", quoteId)}${queryString}`);
        } else if (primaryQuoteId) {
          navigate(
            `${getScopedPageUrl("cloneQuote", primaryQuoteId)}${queryString}`
          );
        } else {
          navigate(
            `${getScopedPageUrl("createQuote", dossierId)}${queryString}`
          );
        }
      }
    },
    onCancel: () => {},
  });

  useEffect(() => {
    if (selectedItem) {
      handleQuoteAction(!!quoteId);
    }
  }, [selectedItem]);

  const onQuote = async (item: VehicleComparisonItem) => {
    setSelectedItem(item);
  };

  const onAIReview = async (item: VehicleComparisonItem) => {
    const params = new URLSearchParams({
      ...(quoteId && { quoteId }),
      ...(hideQuoteButton && { hideQuoteButton: "true" }),
    });

    const queryString = params.toString();

    navigate({
      pathname: getScopedPageUrl(
        "dossierVehicleReview",
        dossierId,
        item.redbookCode
      ),
      search: queryString ? `?${queryString}` : undefined,
    });
  };

  const onAISuggestions = async (item: VehicleComparisonItem) => {
    const _quoteId = quoteId || primaryQuoteId;

    if (
      _quoteId &&
      item.ceMakeId &&
      item.ceModelId &&
      item.ceVariantId &&
      item.year
    ) {
      const queryString = `?makeId=${item.ceMakeId}&modelId=${
        item.ceModelId
      }&modelYear=${item.year}&variantId=${item.ceVariantId}${
        quoteId ? `&editQuote=true` : ""
      }`;
      navigate(
        `${getScopedPageUrl(
          "viewQuoteVehicleSuggestions",
          _quoteId
        )}${queryString}`
      );
    }
  };

  const renderAiReview = (item: VehicleComparisonItem) => {
    return (
      <Box>
        <AIButton
          type="vehicle-review"
          onClick={() => onAIReview(item)}
          shouldPromptForAIConsent={shouldPromptForAIConsent}
          textOverride="AI"
        />
      </Box>
    );
  };

  const renderAiSuggestions = (item: VehicleComparisonItem) => {
    const hasCEVehicleIds =
      !!item.ceMakeId && !!item.ceModelId && !!item.year && !!item.ceVariantId;
    return (
      <Box>
        {hasCEVehicleIds && (
          <AIButton
            type="vehicle-suggestions"
            onClick={() => onAISuggestions(item)}
            shouldPromptForAIConsent={shouldPromptForAIConsent}
            textOverride="AI"
          />
        )}
      </Box>
    );
  };

  const renderRedbookCode = (item: VehicleComparisonItem) => {
    const hasCEVehicleIds =
      !!item.ceMakeId && !!item.ceModelId && !!item.year && !!item.ceVariantId;
    const params = new URLSearchParams({
      ...(quoteId && { quoteId }),
      ...(hideQuoteButton && { hideQuoteButton: "true" }),
    });

    const queryString = params.toString();

    return (
      <Box sx={{ width: "100%" }}>
        <Link
          to={`${getScopedPageUrl(
            "dossierVehicleDetail",
            dossierId,
            item.redbookCode
          )}?${queryString}`}
        >
          <Box sx={{ display: "flex", columnGap: 1, height: "40px" }}>
            <Box
              width="40px"
              height="40px"
              component="img"
              src={item.imageUrl ?? "plac"}
              alt={`${item.make} ${item.model}`}
              sx={{ objectFit: "cover" }}
            />
            <Box>
              <Typography variant="bodySmall">
                {item.make} {item.model}
              </Typography>
            </Box>
          </Box>
        </Link>
        {!hideQuoteButton && (
          <Box
            mt={2}
            sx={{
              display: "flex",
              justifyContent: "center",
              visibility: hasCEVehicleIds ? "visible" : "hidden",
            }}
          >
            <APButton
              text="Quote"
              color="secondary"
              variant="contained"
              onClick={() => onQuote(item)}
            />
          </Box>
        )}
      </Box>
    );
  };

  const buildComparison = (
    comparisons: VehicleComparison[]
  ): Comparison<VehicleComparisonItem> => {
    const items: VehicleComparisonItem[] = comparisons.map((comparison) => {
      const bodyTypeText = standardVehicleValueFormatter(
        "bodyType",
        comparison.variant.bodyType,
        {
          fallbacks: comparison.variant.unMappedValues,
        }
      );

      return {
        redbookCode: comparison.variant.redbookVehicleCode,
        summary: comparison.variant.redbookVehicleCode,
        make: comparison.variant.make,
        model: comparison.variant.model,
        year: comparison.variant.year,
        variant: `${comparison.variant.make} ${comparison.variant.model} ${
          comparison.variant.year
        } ${comparison.variant.variant ?? ""} ${
          comparison.variant.releaseYear
            ? `- Released: ${formatReleaseDate(
                comparison.variant.releaseYear,
                comparison.variant.releaseMonth
              )}`
            : ""
        }`,
        value: comparison.review?.value ?? null,
        comfort: comparison.review?.comfort ?? null,
        performance: comparison.review?.performance ?? null,
        runningCosts: comparison.review?.costToRun ?? null,
        imageUrl:
          comparison.variant.primaryVehicleImage ?? "/photo-placeholder.png",
        aiReview: comparison.variant.redbookVehicleCode,
        aiSuggestions: comparison.variant.redbookVehicleCode,
        ceMakeId: comparison.variant.cemakeId,
        ceModelId: comparison.variant.cemodelId,
        ceVariantId: comparison.variant.cevariantId,

        "vehicle.price": comparison.variant.listPriceIncGst,
        "vehicle.doorAndBodyType": `${comparison.variant.doors} Door${
          bodyTypeText ? ` ${bodyTypeText}` : ""
        }`,
        "vehicle.seats": comparison.variant.seatCapacity,
        "vehicle.ancapRating": comparison.variant.ancaprating
          ? parseInt(comparison.variant.ancaprating)
          : null,
        "vehicle.warrantYrs": comparison.variant.warrantyYears,
        "vehicle.serviceIntervalMths":
          comparison.variant.regularServiceIntervalMonths,
        "vehicle.serviceIntervalKms":
          comparison.variant.regularServiceIntervalKms,
        "vehicle.countryOfOrigin": comparison.variant.countryOfOrigin,

        "powerPlant.powerKw": comparison.variant.powerKw,
        "powerPlant.torque": comparison.variant.torque,
        "powerPlant.powerPlantName": standardVehicleValueFormatter(
          "powerPlantType",
          comparison.variant.powerPlantType,
          {
            fallbacks: comparison.variant.unMappedValues,
          }
        ),
        "powerPlant.acceleration": comparison.variant.acceleration,
        "powerPlant.range": comparison.variant.range,
        "powerPlant.batteryType": comparison.variant.batteryType,
        "powerPlant.driveDescription": standardVehicleValueFormatter(
          "driveDescription",
          comparison.variant.driveDescription,
          {
            fallbacks: comparison.variant.unMappedValues,
          }
        ),
        "powerPlant.fuelType": standardVehicleValueFormatter(
          "fuelType",
          comparison.variant.fuelType,
          {
            fallbacks: comparison.variant.unMappedValues,
          }
        ),

        "payload.minCargoVolume": comparison.variant.minCargoVolume,
        "payload.maxCargoVolume": comparison.variant.maxCargoVolume,
        "payload.towingUnbrakedKg": comparison.variant.towingUnbrakedKg,
        "payload.towingBrakedKg": comparison.variant.towingBrakedKg,
        "payload.payload": comparison.variant.payload,

        "wheels.rimMaterial": comparison.variant.rimMaterial,
        "wheels.frontRim": comparison.variant.frontRim,
        "wheels.rearRim": comparison.variant.rearRim,

        "dimensions.length": comparison.variant.length,
        "dimensions.width": comparison.variant.width,
        "dimensions.height": comparison.variant.height,
        "dimensions.kerbWeight": comparison.variant.kerbWeight,
      };
    });
    const {
      parsedInitialPrimaryCriteria,
      parsedSelectedCriteria,
      summaryCriteria,
    } = parseComparisonParams(
      items[0],
      initialPrimaryCriteria,
      "summary",
      selectedCriteria,
      "summary"
    );

    const criteria: Partial<
      Record<
        keyof VehicleComparisonItem,
        ComparisonCriterion<VehicleComparisonItem>
      >
    > = {
      redbookCode: {
        label: "",
        valueRenderer: (item) => renderRedbookCode(item),
      },
      variant: {
        label: CriteriaLabelMap.variant,
        sortable: {
          direction: "asc",
          highlightWinner: false,
        },
        styles: {
          topAlign: true,
          smallFont: true,
        },
        valueFormatter: (value) => standardValueFormatter(value),
      },
      value: {
        label: CriteriaLabelMap.value,
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) => standardValueFormatter(value),
        getAppLocationId: (_, index) =>
          index === 0
            ? "VehicleCompare-InitialValueRating"
            : "VehicleCompare-Value",
      },
      comfort: {
        label: CriteriaLabelMap.comfort,
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) => standardValueFormatter(value),
        getAppLocationId: () => "VehicleCompare-Comfort",
      },
      performance: {
        label: CriteriaLabelMap.performance,
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) => standardValueFormatter(value),
        getAppLocationId: () => "VehicleCompare-Performance",
      },
      runningCosts: {
        label: CriteriaLabelMap.runningCosts,
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) => standardValueFormatter(value),
        getAppLocationId: () => "VehicleCompare-RunningCost",
      },
      aiReview: {
        label: CriteriaLabelMap.aiReview,
        labelAppLocationId: "VehicleCompare-AIReview",
        labelEndAdornment: (
          <Box>
            <InfoButton />
          </Box>
        ),
        valueRenderer: (item) => renderAiReview(item),
      },
      aiSuggestions: {
        label: CriteriaLabelMap.aiSuggestions,
        labelAppLocationId: "VehicleCompare-AIVehicleSuggestion",
        labelEndAdornment: (
          <Box>
            <InfoButton />
          </Box>
        ),
        valueRenderer: (item) => renderAiSuggestions(item),
      },
      "vehicle.price": {
        label: CriteriaLabelMap["vehicle.price"],
        sortable: {
          direction: "asc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("listPriceIncGst", value),
        getAppLocationId: () => "VehicleCompare-VehiclePrice",
      },
      "vehicle.doorAndBodyType": {
        label: CriteriaLabelMap["vehicle.doorAndBodyType"],
        sortable: {
          direction: "asc",
          highlightWinner: false,
        },
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "vehicle.seats": {
        label: CriteriaLabelMap["vehicle.seats"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("seatCapacity", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "vehicle.ancapRating": {
        label: CriteriaLabelMap["vehicle.ancapRating"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("ancaprating", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "vehicle.warrantYrs": {
        label: CriteriaLabelMap["vehicle.warrantYrs"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("warrantyYears", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "vehicle.serviceIntervalMths": {
        label: CriteriaLabelMap["vehicle.serviceIntervalMths"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("regularServiceIntervalMonths", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "vehicle.serviceIntervalKms": {
        label: CriteriaLabelMap["vehicle.serviceIntervalKms"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("regularServiceIntervalKms", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "vehicle.countryOfOrigin": {
        label: CriteriaLabelMap["vehicle.countryOfOrigin"],
        sortable: {
          direction: "asc",
          highlightWinner: false,
        },
        valueFormatter: (value) => standardValueFormatter(value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "powerPlant.powerKw": {
        label: CriteriaLabelMap["powerPlant.powerKw"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("powerKw", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "powerPlant.torque": {
        label: CriteriaLabelMap["powerPlant.torque"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("torque", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "powerPlant.powerPlantName": {
        label: CriteriaLabelMap["powerPlant.powerPlantName"],
        sortable: {
          direction: "asc",
          highlightWinner: false,
        },
        valueFormatter: (value) => standardValueFormatter(value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "powerPlant.acceleration": {
        label: CriteriaLabelMap["powerPlant.acceleration"],
        sortable: {
          direction: "asc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("acceleration", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "powerPlant.range": {
        label: CriteriaLabelMap["powerPlant.range"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("range", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "powerPlant.batteryType": {
        label: CriteriaLabelMap["powerPlant.batteryType"],
        sortable: {
          direction: "asc",
          highlightWinner: false,
        },
        valueFormatter: (value) => standardValueFormatter(value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "powerPlant.driveDescription": {
        label: CriteriaLabelMap["powerPlant.driveDescription"],
        sortable: {
          direction: "asc",
          highlightWinner: false,
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("driveDescription", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "powerPlant.fuelType": {
        label: CriteriaLabelMap["powerPlant.fuelType"],
        sortable: {
          direction: "asc",
          highlightWinner: false,
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("fuelType", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "payload.minCargoVolume": {
        label: CriteriaLabelMap["payload.minCargoVolume"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("minCargoVolume", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "payload.maxCargoVolume": {
        label: CriteriaLabelMap["payload.maxCargoVolume"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("maxCargoVolume", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "payload.towingUnbrakedKg": {
        label: CriteriaLabelMap["payload.towingUnbrakedKg"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("towingUnbrakedKg", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "payload.towingBrakedKg": {
        label: CriteriaLabelMap["payload.towingBrakedKg"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("towingBrakedKg", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "payload.payload": {
        label: CriteriaLabelMap["payload.payload"],
        sortable: {
          direction: "desc",
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("payload", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "wheels.rimMaterial": {
        label: CriteriaLabelMap["wheels.rimMaterial"],
        sortable: {
          direction: "asc",
          highlightWinner: false,
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("rimMaterial", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "wheels.frontRim": {
        label: CriteriaLabelMap["wheels.frontRim"],
        sortable: {
          direction: "asc",
          highlightWinner: false,
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("frontRim", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "wheels.rearRim": {
        label: CriteriaLabelMap["wheels.rearRim"],
        sortable: {
          direction: "asc",
          highlightWinner: false,
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("rearRim", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "dimensions.length": {
        label: CriteriaLabelMap["dimensions.length"],
        sortable: {
          direction: "desc",
          highlightWinner: false,
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("length", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "dimensions.width": {
        label: CriteriaLabelMap["dimensions.width"],
        sortable: {
          direction: "desc",
          highlightWinner: false,
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("width", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "dimensions.height": {
        label: CriteriaLabelMap["dimensions.height"],
        sortable: {
          direction: "desc",
          highlightWinner: false,
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("height", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
      "dimensions.kerbWeight": {
        label: CriteriaLabelMap["dimensions.kerbWeight"],
        sortable: {
          direction: "desc",
          highlightWinner: false,
        },
        valueFormatter: (value) =>
          standardVehicleValueFormatter("kerbWeight", value),
        getAppLocationId: () => "VehicleCompare-Specs",
      },
    };

    const alwaysShownTopCriteria: (keyof VehicleComparisonItem)[] = [
      "aiReview",
    ];

    if (quoteId || primaryQuoteId) {
      alwaysShownTopCriteria.push("aiSuggestions");
    }

    return {
      items,
      criteria: {
        ...criteria,
        summary: {
          label: CriteriaLabelMap.summary,
          sortable: {
            direction: "desc",
            function: getSummarySortFn(items, criteria, summaryCriteria),
          },
          styles: {
            topAlign: true,
          },
          valueRenderer: (item) => (
            <APComparisonSummary
              selectedItem={item}
              items={items}
              criteria={criteria}
              summaryCriteria={summaryCriteria}
              criteriaLabelMap={CriteriaLabelMap}
            />
          ),
          getAppLocationId: () => "VehicleCompare-Summary",
        },
      },
      headerCriteria: "redbookCode",
      initialPrimaryCriteria: parsedInitialPrimaryCriteria ?? "summary",
      selectedCriteria: ["variant", ...parsedSelectedCriteria],
      alwaysShownTopCriteria: alwaysShownTopCriteria,
      summaryCriteria: "summary",
      expandableCriteria: [
        {
          id: "vehicle",
          label: "Vehicle",
          criteria: [
            "vehicle.price",
            "vehicle.doorAndBodyType",
            "vehicle.seats",
            "vehicle.ancapRating",
            "vehicle.warrantYrs",
            "vehicle.serviceIntervalMths",
            "vehicle.serviceIntervalKms",
            "vehicle.countryOfOrigin",
          ],
          expandByDefault: false,
        },
        {
          id: "powerPlant",
          label: "Power Plant",
          criteria: [
            "powerPlant.powerKw",
            "powerPlant.torque",
            "powerPlant.powerPlantName",
            "powerPlant.acceleration",
            "powerPlant.range",
            "powerPlant.batteryType",
            "powerPlant.driveDescription",
            "powerPlant.fuelType",
          ],
          expandByDefault: false,
        },
        {
          id: "payload",
          label: "Payload",
          criteria: [
            "payload.minCargoVolume",
            "payload.maxCargoVolume",
            "payload.towingUnbrakedKg",
            "payload.towingBrakedKg",
            "payload.payload",
          ],
          expandByDefault: false,
        },
        {
          id: "wheels",
          label: "Wheels",
          criteria: ["wheels.rimMaterial", "wheels.frontRim", "wheels.rearRim"],
          expandByDefault: false,
        },
        {
          id: "dimensions",
          label: "Dimensions",
          criteria: [
            "dimensions.length",
            "dimensions.width",
            "dimensions.height",
            "dimensions.kerbWeight",
          ],
          expandByDefault: false,
        },
      ],
      highlightWinner: true,
      faqProps,
      onSort,
    };
  };

  const isLoading =
    comparisons.isLoading ||
    dossier.isLoading ||
    dossierStatus.isLoading ||
    isLoadingFaqs;
  const isSuccess =
    comparisons.isSuccess &&
    dossier.isSuccess &&
    dossierStatus.isSuccess &&
    isSuccessFaqs;

  const comparison =
    isSuccess && comparisons.data?.length > 0
      ? buildComparison(comparisons.data)
      : null;
  const renderAppLocation = faqProps.renderAppLocation;

  return (
    <PageContainer
      title="Compare Vehicles"
      loading={isLoading}
      titleButton={
        isSuccess ? (
          <Box mr={1}>
            {renderAppLocation(
              "VehicleCompare-Page",
              <InfoButton />,
              true,
              true
            )}
          </Box>
        ) : undefined
      }
      containerRef={containerRef}
    >
      {isSuccess && comparison && (
        <>
          <OverwriteQuoteModal
            isOpen={showOverwriteModal}
            onClose={handleOverwriteCancel}
            onConfirm={handleOverwriteConfirm}
            oldestQuote={oldestQuote}
          />
          <Box mb={2}>
            {renderAppLocation(
              "VehicleCompare-PoweredByAI",
              <PoweredByGemini />,
              false
            )}
          </Box>
          {comparison && (
            <APComparison comparison={comparison} containerRef={containerRef} />
          )}
          <Box
            sx={{
              pt: 4,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Stack
              spacing={2}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Box display="flex" gap={1}>
                <BackButton /> <HomeButton destination="dossiersHome" />
              </Box>
              <RestrictedAppScope scope="external">
                <Box>
                  <APContactHelpButton
                    regardingOpportunityId={
                      dossier.data.opportunity?.id ?? undefined
                    }
                  />
                </Box>
              </RestrictedAppScope>
              <Box>
                <AITermsText subject="Vehicle Ratings" />
              </Box>
            </Stack>
          </Box>
          <Box mt={2}>
            <CopyMarketingUrl type="dossierVehicleComparison" />
          </Box>
        </>
      )}
    </PageContainer>
  );
};

export default DossierVehicleComparison;
